import { useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, RefreshCw, Loader2, X, Copy, CheckCircle2,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";

/** AI-drafted email preview & editor. Sponsor can tweak the subject/body
 *  text directly, switch tone presets, or add a custom angle and ask the AI
 *  to regenerate. Two final actions:
 *   - "Copy to clipboard" -> sponsor pastes into their own email client
 *   - "Save as Executive Summary" -> overwrites deal.executive_summary so
 *     the same copy appears on the public deal page hero / invite email.
 *  We deliberately DO NOT auto-send to investors here — sponsors always
 *  want a final review pass before mass outreach. The existing "Email N"
 *  button still handles the actual send pipeline. */
export default function AiDraftDialog({ draft, onClose, onRegenerate, onChange, busy, deal, onSavedSummary }) {
  const [copyState, setCopyState] = useState("idle");
  const [saving, setSaving] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${draft.subject}\n\n${draft.body}`);
      setCopyState("copied");
      setTimeout(()=>setCopyState("idle"), 1500);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const saveAsExecSummary = async () => {
    if (!draft.body?.trim()) { toast.error("Body is empty"); return; }
    setSaving(true);
    try {
      // Strip the salutation + sign-off so the public page hero reads cleanly
      // (those belong in the email envelope, not the deal summary).
      const cleaned = draft.body
        .replace(/^Hi\s+\{\{[^}]+\}\}\s*,?\s*\n+/i, "")
        .replace(/\n+Best,?\s*\n.*$/i, "")
        .trim();
      await api.put(`/deals/${deal.id}`, { executive_summary: cleaned });
      toast.success("Saved to Executive Summary");
      onSavedSummary?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const TONES = [
    { value: "professional", label: "Professional" },
    { value: "warm", label: "Warm" },
    { value: "urgent", label: "Urgent" },
    { value: "concise", label: "Concise" },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="deal-ai-draft-dialog">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-sm shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-600" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-violet-600">AI Draft · Investor Email</div>
              <div className="text-[10px] font-mono text-slate-400">Model: {draft.model || "—"}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-slate-100 text-slate-500" data-testid="deal-ai-draft-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Subject</div>
            <input value={draft.subject} onChange={(e)=>onChange({ subject: e.target.value })}
              className="input-clean w-full text-sm font-semibold"
              data-testid="ai-draft-subject" />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Body</div>
            <textarea rows={14} value={draft.body} onChange={(e)=>onChange({ body: e.target.value })}
              className="input-clean w-full text-sm leading-relaxed font-mono"
              data-testid="ai-draft-body" />
            <div className="text-[10px] font-mono text-slate-400 mt-1">
              Placeholder <code className="text-violet-600">{"{{investor_first_name}}"}</code> auto-substitutes when each investor receives their copy.
            </div>
          </div>

          {/* Regenerate controls */}
          <div className="border-t border-slate-100 pt-4 space-y-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Regenerate with…</div>
            <div className="flex flex-wrap items-center gap-1.5">
              {TONES.map(t => (
                <button key={t.value} type="button" disabled={busy}
                  onClick={()=>onRegenerate({ tone: t.value })}
                  data-testid={`ai-draft-tone-${t.value}`}
                  className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full border transition-colors disabled:opacity-50
                    ${draft.tone === t.value ? "bg-violet-600 border-violet-700 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-700"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-end gap-2">
              <input value={draft.angle || ""}
                onChange={(e)=>onChange({ angle: e.target.value })}
                onKeyDown={(e)=>{ if (e.key === "Enter" && !busy) onRegenerate({ angle: e.currentTarget.value }); }}
                placeholder="Optional angle (e.g. 'emphasize the value-add upside')"
                className="input-clean w-full text-xs"
                data-testid="ai-draft-angle" />
              <button onClick={()=>onRegenerate({})} disabled={busy}
                data-testid="ai-draft-regenerate"
                className="text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 px-3 py-2 rounded-sm">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Regenerate
              </button>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4 flex items-center justify-end gap-2 flex-wrap">
            <button onClick={onClose}
              className="px-4 py-2 text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-800">
              Close
            </button>
            <button onClick={saveAsExecSummary} disabled={saving} data-testid="ai-draft-save-summary"
              className="border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 disabled:opacity-50 px-4 py-2 rounded-sm text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {saving ? "Saving…" : "Save as Exec Summary"}
            </button>
            <button onClick={copy} data-testid="ai-draft-copy"
              className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-sm text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
              <Copy className="w-3.5 h-3.5" />
              {copyState === "copied" ? "Copied!" : "Copy Subject + Body"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
